import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { User, AuthError, Session } from '@supabase/supabase-js';
import { supabase } from '../utils/supabase';
import toast from 'react-hot-toast';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, username: string) => Promise<{ error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signInWithGoogle: () => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  handleAuthCallback: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const initialLoad = useRef(true);
  const previousSession = useRef<Session | null>(null);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        console.log('Initial session check:', {
          hasSession: !!currentSession,
          userEmail: currentSession?.user?.email,
          emailConfirmed: currentSession?.user?.email_confirmed_at,
          accessToken: currentSession?.access_token ? 'present' : 'missing'
        });

        if (currentSession?.user?.email_confirmed_at) {
          setSession(currentSession);
          setUser(currentSession.user);
          
          // Ensure profile exists for authenticated user
          await ensureProfileExists(currentSession.user.id);
        }
        setLoading(false);

        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
          console.log('Auth state change:', event, {
            userEmail: newSession?.user?.email,
            emailConfirmed: newSession?.user?.email_confirmed_at,
            accessToken: newSession?.access_token ? 'present' : 'missing'
          });
          
          if (event === 'INITIAL_SESSION' && newSession?.user?.email_confirmed_at) {
            setSession(newSession);
            setUser(newSession.user);
            previousSession.current = newSession;
            await ensureProfileExists(newSession.user.id);
          } else if (event === 'TOKEN_REFRESHED' && newSession?.user?.email_confirmed_at) {
            setSession(newSession);
            setUser(newSession.user);
            previousSession.current = newSession;
          } else if (event === 'SIGNED_IN' && newSession?.user?.email_confirmed_at) {
            setSession(newSession);
            setUser(newSession.user);
            
            // Show toast only if this is not initial load and not callback
            if (!initialLoad.current && previousSession.current === null && !window.location.pathname.includes('/auth/callback')) {
              toast.success('Вы паспяхова ўвайшлі ў сістэму');
            }
            
            previousSession.current = newSession;
            await ensureProfileExists(newSession.user.id);
          } else if (event === 'SIGNED_OUT') {
            setSession(null);
            setUser(null);
            previousSession.current = null;
            
            // Show toast only if this is not callback page
            if (!window.location.pathname.includes('/auth/callback')) {
              toast.success('Вы выйшлі з сістэмы');
            }
          }
          initialLoad.current = false;
        });

        return () => {
          subscription.unsubscribe();
        };
      } catch (error) {
        console.error('Error initializing auth:', error);
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // Function to ensure profile exists
  const ensureProfileExists = async (userId: string) => {
    try {
      // Call the database function to create profile if missing
      const { error } = await supabase.rpc('create_profile_if_missing', { user_id: userId });
      if (error) {
        console.warn('Failed to ensure profile exists:', error);
      }
    } catch (error) {
      console.warn('Error ensuring profile exists:', error);
    }
  };

  const signUp = async (email: string, password: string, username: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { 
            username,
            full_name: username,
            name: username
          },
          emailRedirectTo: `${window.location.origin}/auth/callback?type=signup`
        }
      });

      if (error) {
        console.error('SignUp error:', error);
        if (error.message === 'User already registered') {
          throw new Error('Карыстальнік з такім email ужо зарэгістраваны');
        } else if (error.message.includes('Database error')) {
          throw new Error('Памылка базы дадзеных. Калі ласка, паспрабуйце пазней');
        } else if (error.message === 'Error sending confirmation email') {
          throw new Error('Не атрымалася адправіць ліст пацверджання. Калі ласка, паспрабуйце пазней');
        } else {
          throw error;
        }
      }

      // Sign out immediately after signup to enforce email verification
      if (data.user) {
        await supabase.auth.signOut();
      }

      return { error: null };
    } catch (error) {
      console.error('Error in signUp:', error);
      return { error: error as AuthError };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        console.error('SignIn error:', error);
        if (error.message === 'Invalid login credentials') {
          throw new Error('Няправільны email або пароль');
        } else if (error.message.includes('Email not confirmed')) {
          throw new Error('Калі ласка, пацвердзіце ваш email перад уваходам');
        } else {
          throw error;
        }
      }

      if (!data.user?.email_confirmed_at) {
        await supabase.auth.signOut();
        throw new Error('Калі ласка, пацвердзіце ваш email перад уваходам');
      }

      return { error: null };
    } catch (error) {
      console.error('Error in signIn:', error);
      return { error: error as AuthError };
    }
  };

  const signInWithGoogle = async () => {
    try {
      console.log('Starting Google OAuth...');
      
      // Clear any existing auth state to prevent conflicts
      await supabase.auth.signOut();
      
      // Wait a bit to ensure cleanup is complete
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          }
        }
      });

      console.log('Google OAuth response:', { data, error });

      if (error) {
        console.error('Google OAuth error:', error);
        throw error;
      }

      // ВАЖНО: НЕ делаем никакой навигации после signInWithOAuth
      // Supabase сам редиректит пользователя
      console.log('Google OAuth initiated successfully, waiting for redirect...');
      
      return { error: null };
    } catch (error) {
      console.error('Error in signInWithGoogle:', error);
      return { error: error as AuthError };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      // Clear local state
      setSession(null);
      setUser(null);
      
      // Clear any stored auth data
      localStorage.removeItem('supabase.auth.token');
      
      // Reload the page to ensure clean state
      window.location.href = '/';
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Памылка пры выхадзе з сістэмы');
    }
  };

  const resetPassword = async (email: string) => {
    return supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/callback?type=recovery`
    });
  };

  const handleAuthCallback = async () => {
    try {
      const { data: { session: newSession }, error } = await supabase.auth.getSession();
      if (error) throw error;

      console.log('Auth callback session check:', {
        hasSession: !!newSession,
        userEmail: newSession?.user?.email,
        emailConfirmed: newSession?.user?.email_confirmed_at,
        accessToken: newSession?.access_token ? 'present' : 'missing'
      });

      if (newSession?.user?.email_confirmed_at) {
        setSession(newSession);
        setUser(newSession.user);
        await ensureProfileExists(newSession.user.id);
      }
    } catch (error) {
      console.error('Error in handleAuthCallback:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      loading,
      signUp,
      signIn,
      signInWithGoogle,
      signOut,
      resetPassword,
      handleAuthCallback
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};